/* frontend/src/components/VoteForNextSection.jsx
   ------------------------------------------------------------------
   "Vote for what's next" — shared between Dashboard.jsx and Skills.jsx
   so both pages render the exact same heading + Technical Translation
   announcement, not two copies that can drift out of sync.

   What sits below that announcement differs by page, via `variant`:
   - 'leaderboard' (Skills) renders the Translation card and a bordered
     "EARLY-STAGE IDEAS" panel of chips side by side, align-items:
     stretch so both columns share the row's height (driven by
     whichever is naturally taller) rather than the panel just running
     past the card's bottom edge. Stacks vertically under 900px. The
     panel is a new ancestor around <SkillLeaderboard/>, not a wrapper
     around individual chips — those stay exactly as SkillLeaderboard.jsx
     renders them, and the panel itself carries no height/overflow
     constraint, since FLIP needs chips free to translate during a
     reorder without being clipped or squeezed to fit — any leftover
     height inside the panel is left as whitespace, not force-filled.
   - 'link' (Dashboard) renders one line pointing at the leaderboard
     section on Skills instead. There must be exactly one
     postgres_changes subscription to skill_candidates at a time, so the
     live chips and their Realtime channel exist on Skills only —
     Dashboard does not mount a second copy of the leaderboard.

   IdeaPoll.jsx (the old early-stage-ideas poll, same 8 ids as
   SkillLeaderboard's candidates) has been retired — it tallied
   skill_requests client-side, which RLS now scopes to the viewer's own
   rows, so its counts were never right for anyone but the voter. Its row
   styling lives on in SkillLeaderboard's chips.

   `source` distinguishes which page a vote came from in the
   skill_requests.reason column (e.g. 'skills_page_vote') — same
   convention the old wiring used.
*/
import { Link } from 'react-router-dom';
import TranslatorSkillCard from './TranslatorSkillCard';
import SkillLeaderboard from './SkillLeaderboard';

export const LEADERBOARD_ANCHOR = 'vote-leaderboard';

const VoteForNextSection = ({ skills, isDark, headColor, mutedColor, source, variant = 'leaderboard' }) => {
  const comingSoon = skills.filter((s) => s.status === 'coming-soon');
  const translation = comingSoon.find((s) => s.id === 'technical-translation');
  const accent = '#ea580c';

  return (
    <div id={variant === 'leaderboard' ? LEADERBOARD_ANCHOR : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 900, color: headColor, margin: 0, letterSpacing: '-0.02em' }}>
          Vote for what's next
        </h2>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: mutedColor }}>
          {comingSoon.length} in development
        </div>
      </div>
      <p style={{ fontSize: 13, color: mutedColor, margin: '6px 0 0', lineHeight: 1.5 }}>
        Each vote directly influences our build order. Most voted ships next.
      </p>

      {variant === 'leaderboard' ? (
        <>
          <style>{`
            .vfn-row { display: flex; align-items: stretch; gap: 24px; margin-top: 20px; }
            .vfn-translator { flex: 0 1 460px; max-width: 460px; min-width: 280px; }
            .vfn-ideas { flex: 1; min-width: 280px; }
            @media (max-width: 900px) {
              .vfn-row { flex-direction: column; }
              .vfn-translator { max-width: none; }
            }
          `}</style>
          <div className="vfn-row">
            {translation && (
              <div className="vfn-translator">
                <TranslatorSkillCard skill={translation} isDark={isDark} />
              </div>
            )}
            <div
              className="vfn-ideas"
              style={{
                borderRadius: 16, padding: '16px 18px', boxSizing: 'border-box',
                background: isDark ? '#1c1917' : '#ffffff',
                border: `1px solid ${isDark ? '#2d2825' : '#e8e4df'}`,
              }}
            >
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: mutedColor, textTransform: 'uppercase', marginBottom: 12 }}>
                Early-stage ideas
              </div>
              <SkillLeaderboard isDark={isDark} headColor={headColor} mutedColor={mutedColor} source={source} />
            </div>
          </div>
        </>
      ) : (
        <>
          {translation && (
            <div style={{ marginTop: 20, maxWidth: 460 }}>
              <TranslatorSkillCard skill={translation} isDark={isDark} />
            </div>
          )}
          <Link
            to={`/skills#${LEADERBOARD_ANCHOR}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 16,
              fontSize: 13, fontWeight: 700, color: accent, textDecoration: 'none',
            }}
          >
            8 ideas up for vote →
          </Link>
        </>
      )}
    </div>
  );
};

export default VoteForNextSection;
